import React, { useEffect, useState } from "react";
import { Network, Search, RotateCcw, ArrowUp, ArrowDown, ArrowLeft, Info, Activity, ShieldAlert } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

interface InterfaceData {
    id: number;
    port_index: number;
    port_number: string;
    port_name: string;
    port_description: string | null;
    port_status: string;
    port_speed: string;
    mac_address: string | null;
    vlan: number | null;
    connection_type: string | null;
    mtu: number | null;
    admin_status: string | null;
    interface_type: string | null;
    in_octets: number | null;
    out_octets: number | null;
    in_unicast_packets: number | null;
    out_unicast_packets: number | null;
    in_non_unicast_packets: number | null;
    out_non_unicast_packets: number | null;
    in_errors: number | null;
    out_errors: number | null;
    in_discards: number | null;
    out_discards: number | null;
    packet_loss_in_total_packets: number | null;
    packet_loss_out_total_packets: number | null;
    packet_loss_in_lost_packets: number | null;
    packet_loss_out_lost_packets: number | null;
    packet_loss_in_loss_percentage: number | null;
    packet_loss_out_loss_percentage: number | null;
    native_vlan: number | null;
    raw_speed_bps: number | null;
    is_promoted: boolean | null;
    last_change?: string | null;
    in_octets_64bit?: number | null;
    out_octets_64bit?: number | null;
    connected_asset_id?: number | null;
    oper_status: string | null;
}

interface DiscoveredInterfacesProps {
    hideHeader?: boolean;
    defaultItemsPerPage?: number;
    compact?: boolean;
    onDataLoaded?: (hasData: boolean) => void;
}

const formatBytes = (bytes: number | null) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const DiscoveredInterfaces: React.FC<DiscoveredInterfacesProps> = ({
    hideHeader = false,
    defaultItemsPerPage = 10,
    compact = false,
    onDataLoaded
}) => {
    const [data, setData] = useState<InterfaceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // View State: Table or Details
    const [selectedInterface, setSelectedInterface] = useState<InterfaceData | null>(null);

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [summary, setSummary] = useState<{
        rx_traffic_total?: string;
        tx_traffic_total?: string;
        rx_errors?: string;
        tx_errors?: string;
        rx_loss_pct?: string;
        tx_loss_pct?: string;
    } | null>(null);

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = info.asset_id;
    const client_id = info.client_id;


    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = { switch_asset_id: asset_id, client_id: client_id, interface_type: "ethernetCsmacd", };
            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { port_name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { port_number: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { mac_address: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/discovered_interfaces", { params });

            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
                setSummary(response.data.summary || null);
                if (onDataLoaded) onDataLoaded((response.data.data || []).length > 0);
            } else {
                showError(response.data.message);
                if (onDataLoaded) onDataLoaded(false);
            }
        } catch (error: any) {
            showError(error.message);
            if (onDataLoaded) onDataLoaded(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
            setCurrentPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        if (!selectedInterface) {
            fetchData();
        }
    }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm, selectedInterface]);



    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const SortIcon = ({ column }: { column: string }) => {
        if (sortConfig?.key !== column) {
            return (
                <div className="opacity-0 group-hover:opacity-40 transition-opacity">
                    <ArrowUp size={10} className="text-slate-400" />
                </div>
            );
        }
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={10} className="text-orange-500" />
            : <ArrowDown size={10} className="text-orange-500" />;
    };

    const renderDetailRow = (label: string, value: any, suffix: string = "", color: string = "text-slate-200") => (
        <div className="flex justify-between items-center py-1 border-b border-slate-800/50 last:border-0 hover:bg-slate-800/30 px-1 -mx-1 rounded transition-colors">
            <span className={`text-[9px] font-bold ${color} uppercase tracking-widest`}>{label}</span>
            <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value)}>
                {value !== null && value !== undefined && value !== "" ? `${value}${suffix}` : "-"}
            </span>
        </div>
    );

    if (selectedInterface) {
        return (
            <div className="space-y-3 animate-fade-in">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-slate-900/50 p-2.5 rounded-xl border border-slate-800">
                    <div className="flex items-center gap-2.5">
                        <button
                            onClick={() => setSelectedInterface(null)}
                            className="p-1.5 bg-orange-500/50 border border-orange-500/50 rounded-md text-slate-200 hover:text-white hover:border-orange-500/50 transition-all group"
                        >
                            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                        </button>
                        <div className="bg-orange-500/10 p-1.5 rounded-md border border-orange-500/20">
                            <Info className="w-4 h-4 text-orange-500" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-sm font-bold text-white leading-none">
                                    {selectedInterface.port_name || selectedInterface.port_number || 'Unknown Interface'}
                                </h2>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-black uppercase shadow-sm ${selectedInterface.port_status === 'up' && selectedInterface.connected_asset_id != null
                                    ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20'
                                    : selectedInterface.port_status === 'up'
                                        ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                        : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'
                                    }`}>
                                    {selectedInterface.port_status === 'up' && selectedInterface.connected_asset_id != null
                                        ? 'Used'
                                        : selectedInterface.port_status}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                                <span className="text-[10px] text-white font-mono tracking-tighter">
                                    {selectedInterface.mac_address || 'No MAC Address'}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1 text-xs font-bold">
                        <div className="text-slate-200">Last Change: </div>
                        <div className="text-gray-200">{selectedInterface.last_change || 'Never'}</div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    <div className="bg-slate-900/50 p-3 rounded-lg border border-slate-800/50 shadow-inner h-full">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-slate-800 pb-1.5">
                            <Network className="w-3.5 h-3.5 text-orange-500" />
                            <h4 className="text-orange-500 text-[10px] font-black uppercase tracking-widest">Configuration</h4>
                        </div>
                        <div className="space-y-0.5">
                            {renderDetailRow("Admin Status", selectedInterface.admin_status)}
                            {renderDetailRow("Port Speed", selectedInterface.port_speed)}
                            {renderDetailRow("Raw Speed (bps)", selectedInterface.raw_speed_bps)}
                            {renderDetailRow("MTU (bytes)", selectedInterface.mtu)}
                            {renderDetailRow("VLAN", selectedInterface.vlan)}
                            {renderDetailRow("Native VLAN", selectedInterface.native_vlan)}
                        </div>
                    </div>

                    <div className="lg:col-span-2 bg-slate-900/50 p-3 rounded-lg border border-slate-800/50 shadow-inner h-full">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-slate-800 pb-1.5">
                            <Activity className="w-3.5 h-3.5 text-sky-500" />
                            <h4 className="text-sky-500 text-[10px] font-black tracking-widest">TRAFFIC & PACKETS (Rx/Tx)</h4>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-6">
                            <div className="flex-1 space-y-0.5">
                                {renderDetailRow("RX Traffic", formatBytes(selectedInterface.in_octets))}
                                {renderDetailRow("RX Unicast Packets", selectedInterface.in_unicast_packets?.toLocaleString())}
                                {renderDetailRow("RX Non-Unicast Packets", selectedInterface.in_non_unicast_packets?.toLocaleString())}
                                {renderDetailRow("Rx Errors", selectedInterface.in_errors?.toLocaleString(), "", "text-rose-400/90")}
                                {renderDetailRow("Rx Discards", selectedInterface.in_discards?.toLocaleString(), "", "text-rose-400/90")}
                                {renderDetailRow("Loss Rx", selectedInterface.packet_loss_in_loss_percentage, "%", "text-rose-400/90")}
                                {renderDetailRow("Total RX", selectedInterface.in_octets_64bit?.toLocaleString())}
                            </div>
                            <div className="flex-1 space-y-0.5">
                                {renderDetailRow("TX Traffic", formatBytes(selectedInterface.out_octets))}
                                {renderDetailRow("TX Unicast Packets", selectedInterface.out_unicast_packets?.toLocaleString())}
                                {renderDetailRow("TX Non-Unicast Packets", selectedInterface.out_non_unicast_packets?.toLocaleString())}
                                {renderDetailRow("Tx Errors", selectedInterface.out_errors?.toLocaleString(), "", "text-rose-400/90")}
                                {renderDetailRow("Tx Discards", selectedInterface.out_discards?.toLocaleString(), "", "text-rose-400/90")}
                                {renderDetailRow("Loss Tx", selectedInterface.packet_loss_out_loss_percentage, "%", "text-rose-400/90")}
                                {renderDetailRow("Total TX", selectedInterface.out_octets_64bit?.toLocaleString())}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                    <div className="lg:col-span-3 mt-1 bg-slate-900/50 p-3 rounded-lg border border-slate-800/50 shadow-inner">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-slate-800 pb-1.5">
                            <Info className="w-3.5 h-3.5 text-slate-400" />
                            <h4 className="text-slate-400 text-[10px] font-black uppercase tracking-widest">System Metadata</h4>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                            {renderDetailRow("Index", selectedInterface.port_index)}
                            {renderDetailRow("Interface Type", selectedInterface.interface_type)}
                            {renderDetailRow("Conn Type", selectedInterface.connection_type)}
                            <div className="lg:col-span-2">
                                <span className="text-[9px] font-bold text-slate-200 uppercase tracking-widest block mb-1">Description</span>
                                <div className="bg-slate-950/60 p-2.5 rounded border border-slate-800 text-[10px] text-slate-300 font-medium min-h-[40px]">
                                    {selectedInterface.port_description && selectedInterface.port_description !== '""' ? selectedInterface.port_description : "No description provided"}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {summary && !selectedInterface && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                    {/* RX Traffic Card */}
                    <div className="bg-[#0B0F19]/60 border border-slate-800/60 rounded-xl p-3 shadow-lg group hover:border-blue-500/30 transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <ArrowDown className="w-3.5 h-3.5 text-blue-400" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-blue-400 transition-colors">RX Traffic</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-bold text-white tracking-tight truncate" title={summary.rx_traffic_total}>
                                {summary.rx_traffic_total}
                            </h3>
                        </div>
                    </div>

                    {/* TX Traffic Card */}
                    <div className="bg-[#0B0F19]/60 border border-slate-800/60 rounded-xl p-3 shadow-lg group hover:border-orange-500/30 transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <ArrowUp className="w-3.5 h-3.5 text-orange-400" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-orange-400 transition-colors">TX Traffic</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-bold text-white tracking-tight truncate" title={summary.tx_traffic_total}>
                                {summary.tx_traffic_total}
                            </h3>
                        </div>
                    </div>

                    {/* RX Errors Card */}
                    <div className="bg-[#0B0F19]/60 border border-slate-800/60 rounded-xl p-3 shadow-lg group hover:border-rose-500/30 transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <ShieldAlert className="w-3.5 h-3.5 text-rose-500" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-rose-400 transition-colors">RX Errors</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-bold text-rose-500 tracking-tight truncate" title={summary.rx_errors}>
                                {summary.rx_errors}
                            </h3>
                        </div>
                    </div>

                    {/* TX Errors Card */}
                    <div className="bg-[#0B0F19]/60 border border-slate-800/60 rounded-xl p-3 shadow-lg group hover:border-rose-400/30 transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <ShieldAlert className="w-3.5 h-3.5 text-rose-400" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-rose-400 transition-colors">TX Errors</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-bold text-rose-400 tracking-tight truncate" title={summary.tx_errors}>
                                {summary.tx_errors}
                            </h3>
                        </div>
                    </div>

                    {/* RX Loss Card */}
                    <div className="bg-[#0B0F19]/60 border border-slate-800/60 rounded-xl p-3 shadow-lg group hover:border-purple-500/30 transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <Activity className="w-3.5 h-3.5 text-purple-400" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-purple-400 transition-colors">RX Loss %</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-bold text-white tracking-tight truncate" title={summary.rx_loss_pct}>
                                {summary.rx_loss_pct}
                            </h3>
                        </div>
                    </div>

                    {/* TX Loss Card */}
                    <div className="bg-[#0B0F19]/60 border border-slate-800/60 rounded-xl p-3 shadow-lg group hover:border-purple-400/30 transition-all duration-300">
                        <div className="flex items-center gap-1.5 mb-2">
                            <Activity className="w-3.5 h-3.5 text-purple-400" />
                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest group-hover:text-purple-400 transition-colors">TX Loss %</p>
                        </div>
                        <div className="flex items-baseline gap-1.5">
                            <h3 className="text-sm font-bold text-white tracking-tight truncate" title={summary.tx_loss_pct}>
                                {summary.tx_loss_pct}
                            </h3>
                        </div>
                    </div>
                </div>
            )}

            <div className="space-y-4 animate-fade-in">
                {!hideHeader && (
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                        <div className="flex items-center">
                            <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                                <Network className="w-4 h-4 text-white" />
                            </div>
                            <h2 className="text-sm font-bold text-white leading-tight">Discovered Interfaces</h2>
                        </div>
                        <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                            <div className="relative w-full sm:w-64">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-200 w-4 h-4" />
                                <input
                                    type="text"
                                    placeholder="Search interfaces..."
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button
                                onClick={() => fetchData()}
                                className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-200 hover:text-white"
                            >
                                <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                        </div>
                    </div>
                )}

                <div className={`bg-slate-900/50 rounded-xl border border-slate-800 shadow-xl overflow-hidden ${compact ? 'border-orange-500/10' : ''}`}>
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-900/80 border-b border-slate-800">
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('port_name')}>
                                        <div className="flex items-center gap-0.5">Port Name<SortIcon column="port_name" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('port_index')}>
                                        <div className="flex items-center gap-0.5">Port Index<SortIcon column="port_index" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('mac_address')}>
                                        <div className="flex items-center gap-0.5">MAC Address<SortIcon column="mac_address" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('port_status')}>
                                        <div className="flex items-center gap-0.5">Status<SortIcon column="port_status" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('admin_status')}>
                                        <div className="flex items-center gap-0.5">IfAdminStatus<SortIcon column="admin_status" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('oper_status')}>
                                        <div className="flex items-center gap-0.5">IfOperStatus<SortIcon column="oper_status" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap" onClick={() => handleSort('port_speed')}>
                                        <div className="flex items-center gap-0.5">Speed<SortIcon column="port_speed" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('in_octets')}>
                                        <div className="flex items-center justify-end gap-0.5">Rx Traffic (Total)<SortIcon column="in_octets" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('out_octets')}>
                                        <div className="flex items-center justify-end gap-0.5">Tx Traffic (Total)<SortIcon column="out_octets" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('in_errors')}>
                                        <div className="flex items-center justify-end gap-0.5">Rx Errors<SortIcon column="in_errors" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('out_errors')}>
                                        <div className="flex items-center justify-end gap-0.5">Tx Errors<SortIcon column="out_errors" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('packet_loss_in_loss_percentage')}>
                                        <div className="flex items-center justify-end gap-0.5">Rx Loss %<SortIcon column="packet_loss_in_loss_percentage" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('packet_loss_out_loss_percentage')}>
                                        <div className="flex items-center justify-end gap-0.5">Tx Loss %<SortIcon column="packet_loss_out_loss_percentage" /></div>
                                    </th>
                                    <th className="px-4 py-2.5 text-[10px] font-bold text-gray-200 uppercase tracking-widest cursor-pointer group whitespace-nowrap text-right" onClick={() => handleSort('last_change')}>
                                        <div className="flex items-center justify-end gap-0.5">Last Change<SortIcon column="last_change" /></div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                                {loading ? (
                                    <tr><td colSpan={14} className="py-10 text-center"><RotateCcw className="w-6 h-6 text-orange-500 animate-spin mx-auto" /></td></tr>
                                ) : data.length === 0 ? (
                                    <tr><td colSpan={14} className="py-10 text-center text-gray-200 text-xs">No interfaces found</td></tr>
                                ) : (
                                    data.map((item) => (
                                        <tr key={item.id} onClick={() => setSelectedInterface(item)} className="hover:bg-gray-700/50 transition-colors cursor-pointer" title="Click to view full interface details">
                                            <td className="px-4 py-2 text-[11px] font-bold text-white whitespace-nowrap">{item.port_name || '-'}</td>
                                            <td className="px-4 py-2 text-[11px] font-bold text-white whitespace-nowrap">{item.port_index ?? '-'}</td>
                                            <td className="px-4 py-2 text-[11px] text-slate-200 font-mono whitespace-nowrap">{item.mac_address || '-'}</td>
                                            <td className="px-4 py-2 text-[11px] whitespace-nowrap">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shadow-sm ${item.port_status === 'up' && item.connected_asset_id ? 'bg-green-500/10 text-green-500 border border-green-500/20' : item.port_status === 'up' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'}`}>
                                                    {item.port_status === 'up' && item.connected_asset_id != null ? 'Used' : item.port_status}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-[11px] text-slate-300 font-medium whitespace-nowrap">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shadow-sm ${item.admin_status === 'up' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'}`}>
                                                    {item.admin_status || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-[11px] text-slate-300 font-medium whitespace-nowrap">
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shadow-sm ${item.oper_status === 'up' ? 'bg-green-500/10 text-green-500 border border-green-500/20' : 'bg-rose-500/10 text-rose-500 border border-rose-500/20'}`}>
                                                    {item.oper_status || '-'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-2 text-[11px] text-slate-300 font-medium whitespace-nowrap">{item.port_speed || '-'}</td>
                                            <td className="px-4 py-2 text-[11px] text-blue-400 font-mono text-right whitespace-nowrap"><span className="mr-8">{formatBytes(item.in_octets)}</span></td>
                                            <td className="px-4 py-2 text-[11px] text-orange-400 font-mono text-right whitespace-nowrap"><span className="mr-8">{formatBytes(item.out_octets)}</span></td>
                                            <td className={`px-4 py-2 text-[11px] font-mono text-right whitespace-nowrap ${item.in_errors && item.in_errors > 0 ? 'text-rose-500' : 'text-slate-200'}`}><span className="mr-8">{item.in_errors?.toLocaleString() || '0'}</span></td>
                                            <td className={`px-4 py-2 text-[11px] font-mono text-right whitespace-nowrap ${item.out_errors && item.out_errors > 0 ? 'text-rose-500' : 'text-slate-200'}`}><span className="mr-8">{item.out_errors?.toLocaleString() || '0'}</span></td>
                                            <td className={`px-4 py-2 text-[11px] mr-8 font-mono text-right whitespace-nowrap ${item.packet_loss_in_loss_percentage != null && item.packet_loss_in_loss_percentage > 0 ? "text-rose-500" : item.packet_loss_in_loss_percentage === null ? "text-gray-200" : "text-emerald-500"}`}><span className="mr-8">{item.packet_loss_in_loss_percentage != null ? `${item.packet_loss_in_loss_percentage}%` : "-"}</span></td>
                                            <td className={`px-4 py-2 text-[11px] mr-8 font-mono text-right whitespace-nowrap ${item.packet_loss_out_loss_percentage != null && item.packet_loss_out_loss_percentage > 0 ? "text-rose-500" : item.packet_loss_out_loss_percentage === null ? "text-gray-200" : "text-emerald-500"}`}><span className="mr-8">{item.packet_loss_out_loss_percentage != null ? `${item.packet_loss_out_loss_percentage}%` : "-"}</span></td>
                                            <td className={`px-4 py-2 text-[9px] text-right whitespace-nowrap ${item.out_errors && item.out_errors > 0 ? 'text-rose-500' : 'text-slate-200'}`}><span>{item.last_change || 'Never'}</span></td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-gray-900/40 px-4 py-1.5 border-t border-gray-700 flex justify-between items-center">
                        <div className="flex items-center gap-3">
                            <ItemsPerPageDropdown value={itemsPerPage} onChange={(val) => { setItemsPerPage(val); setCurrentPage(1); }} />
                            <span className="text-[10px] text-gray-200 font-medium whitespace-nowrap">Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                            <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="p-1 text-gray-200 hover:text-white disabled:opacity-30 transition-colors"><ArrowUp className="w-3.5 h-3.5 -rotate-90" /></button>
                            <div className="bg-orange-600/10 border border-orange-500/20 text-orange-500 px-2 py-0.5 rounded text-[10px] font-bold">{currentPage}</div>
                            <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages || totalPages === 0} className="p-1 text-gray-200 hover:text-white disabled:opacity-30 transition-colors"><ArrowDown className="w-3.5 h-3.5 -rotate-90" /></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiscoveredInterfaces;
